/* eslint-disable max-len */
import { type FC } from "react";
import { generatePath, matchRoutes, useLocation } from "react-router-dom";

import { Tabs, Tab } from "@snapchat/snap-design-system";

import { Visible } from "../../common/components";
import { NoResults, NoResultsCard } from "../../groups/components";
import { GroupsContainerV2 } from "../../groups/containers";
import { EnabledLensSourcesListContainer } from "../../lens-source/container";
import { AllLensesContainerV2 } from "../../lenses/containers";
import { useSelectedOrganization } from "../../organizations/hooks";
// import { AppRoute } from "../../routes/types";

import { CardWithBannerSkeletonContainer } from "./CardWithBannerSkeletonContainer";
import { tabPanelStyle, tabStyle, tabsContainerStyle, tabsStyle } from "./Dashboard.styles";
import { LensSourcesSkeletonContainer } from "./LensSourcesSkeletonContainer";
import { TabLink } from "./TabLink";
import { AbsoluteRoute } from "../../../core/routes";

interface DashboardTabsProps {
    onNewLensGroup: () => void;
    loading: boolean;
}

export const TabsRoutes = [
    AbsoluteRoute.ORG_LENS_SCHEDULER_GROUPS,
    AbsoluteRoute.ORG_LENS_SCHEDULER_LENSES,
    AbsoluteRoute.ORG_LENS_SCHEDULER_SOURCES,
];

export const DashboardTabs: FC<DashboardTabsProps> = ({ onNewLensGroup, loading }) => {
    const { permissions } = useSelectedOrganization();
    const { pathname } = useLocation();
    const matchedPath = matchRoutes(
        [
            { path: AbsoluteRoute.ORG_LENS_SCHEDULER_GROUPS, end: true },
            { path: AbsoluteRoute.ORG_LENS_SCHEDULER_LENSES, end: true },
            { path: AbsoluteRoute.ORG_LENS_SCHEDULER_SOURCES, end: true },
        ],
        pathname,
    );

    const tabIndex = TabsRoutes.indexOf(matchedPath?.[0]?.route.path ?? AbsoluteRoute.ORG_LENS_SCHEDULER_GROUPS) ?? 0;
    const organizationId = matchedPath?.[0]?.params?.["orgId"] ?? "";
    return (
        <Tabs
            tabsClassName={tabsStyle}
            className={tabsContainerStyle}
            tabPanelClassName={tabPanelStyle}
            defaultIndex={tabIndex}
        >
            <Tab
                tabClassName={tabStyle}
                title={
                    <TabLink to={generatePath(AbsoluteRoute.ORG_LENS_SCHEDULER_GROUPS, { orgId: organizationId })}>
                        Lens Groups
                    </TabLink>
                }
            >
                <CardWithBannerSkeletonContainer loading={loading}>
                    <GroupsContainerV2 onNewLensGroup={onNewLensGroup} />
                </CardWithBannerSkeletonContainer>
            </Tab>
            <Tab
                title={
                    <TabLink to={generatePath(AbsoluteRoute.ORG_LENS_SCHEDULER_LENSES, { orgId: organizationId })}>
                        All Lenses
                    </TabLink>
                }
                tabClassName={tabStyle}
            >
                <CardWithBannerSkeletonContainer loading={loading}>
                    <AllLensesContainerV2 />
                </CardWithBannerSkeletonContainer>
            </Tab>
            <Tab
                tabClassName={tabStyle}
                title={
                    <TabLink to={generatePath(AbsoluteRoute.ORG_LENS_SCHEDULER_SOURCES, { orgId: organizationId })}>
                        Lens Sources
                    </TabLink>
                }
            >
                <LensSourcesSkeletonContainer loading={loading}>
                    <Visible when={!permissions?.readLensSources}>
                        <NoResultsCard titleBorder={false} padBody={false}>
                            <NoResults description="It looks like you don't have permission to view this page." />
                        </NoResultsCard>
                    </Visible>
                    <Visible when={permissions?.readLensSources ?? false}>
                        <EnabledLensSourcesListContainer />
                    </Visible>
                </LensSourcesSkeletonContainer>
            </Tab>
        </Tabs>
    );
};
